import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search,
  LayoutDashboard, 
  FileText, 
  Building2, 
  GraduationCap,
  Briefcase,
  Receipt,
  Users,
  Upload,
  MessageSquare,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  Crown,
  UserCog,
  History,
  Settings,
  CheckCircle
} from 'lucide-react';

const HelpTopic = ({ question, answer, roles }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b last:border-b-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left hover:text-lime transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-medium">{question}</span>
          {roles && (
            <div className="flex gap-1">
              {roles.map(role => (
                <Badge key={role} variant="outline" className="text-[10px] py-0">
                  {role}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
      </button>
      {isOpen && (
        <div className="pb-4 text-muted-foreground text-sm whitespace-pre-line">
          {answer}
        </div>
      )}
    </div>
  );
};

const HelpSection = ({ title, icon: Icon, description, topics }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-lime/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-lime" />
        </div>
        <div>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {topics.map((topic, index) => (
        <HelpTopic key={index} question={topic.question} answer={topic.answer} roles={topic.roles} />
      ))}
    </CardContent>
  </Card>
);

const RoleCard = ({ title, icon: Icon, color, permissions }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      <ul className="space-y-2">
        {permissions.map((perm, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span>{perm}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const roleGuides = [
    {
      title: 'Super Admin',
      icon: Crown,
      color: 'bg-amber-100 text-amber-700',
      permissions: [
        'Full access to all system features',
        'Manage domain whitelist and settings',
        'Promote PMs to Admin and Admins to Super Admin',
        'Approve/deny all user registrations',
        'View complete audit logs',
        'Manage all user accounts (activate, deactivate, delete)',
        'Cannot be disabled by regular admins'
      ]
    },
    {
      title: 'Admin',
      icon: Shield,
      color: 'bg-red-100 text-red-700',
      permissions: [
        'Manage Training Projects, Sessions, Partners, TSPs',
        'Manage Purchase Orders',
        'Approve/deny user registrations (except Super Admins)',
        'Manage user accounts (except Super Admins)',
        'View audit logs',
        'Configure domain settings'
      ]
    },
    {
      title: 'Project Manager (PM)',
      icon: Users,
      color: 'bg-blue-100 text-blue-700',
      permissions: [
        'Manage assigned Training Projects',
        'Create and edit sessions',
        'Submit feedback for delivered sessions',
        'View assigned Partners only',
        'View Courses and TSPs',
        'Cannot access User Management, PO Management, or Settings'
      ]
    }
  ];

  const helpData = [
    {
      title: 'Training Delivery Dashboard',
      icon: LayoutDashboard,
      description: 'Overview of your training management activities',
      topics: [
        { 
          question: 'What information is shown on the Dashboard?', 
          answer: 'The Dashboard provides a comprehensive overview of your training management activities including:\n• Total training projects\n• Active sessions\n• Partner count\n• Key statistics\n\nYou can click on any statistic to see detailed information.' 
        },
        { 
          question: 'How do I navigate from the Dashboard?', 
          answer: 'Click on any card or statistic on the Dashboard to drill down into the details. You can also use the sidebar navigation to access different sections of the application.' 
        }
      ]
    },
    {
      title: 'Training Projects',
      icon: FileText,
      description: 'Managing training delivery projects',
      topics: [
        { 
          question: 'How do I create a new Training Project?', 
          answer: 'Navigate to Training Projects and click the "Create Training Project" button. Fill in the required fields including title, partner, solution area, and solution play.',
          roles: ['Admin', 'Team Manager', 'PM']
        },
        { 
          question: 'What is the Training Project workflow?', 
          answer: 'Training Projects follow a workflow:\n\n1. Planning → Initial setup\n2. Scheduled → Sessions planned\n3. In Progress → Training delivery ongoing\n4. Completed → All done\n\nA project can only be marked as Completed after all sessions are delivered and feedback is submitted.'
        },
        { 
          question: 'How do I add sessions to a Training Project?', 
          answer: 'Open a Training Project and navigate to the Sessions tab. Click "Add Session" to add individual sessions, or use "Import Sessions" to bulk upload sessions from an Excel template.'
        }
      ]
    },
    {
      title: 'Bulk Session Import',
      icon: Upload,
      description: 'Importing multiple sessions from Excel',
      topics: [
        { 
          question: 'How do I bulk import sessions?', 
          answer: '1. Open a Training Project\n2. Go to the Sessions tab\n3. Click "Import Sessions"\n4. Download the Excel template\n5. Fill in your session data following the format guidelines\n6. Upload the file'
        },
        { 
          question: 'What fields are required in the import template?', 
          answer: 'Required fields:\n• Session Title\n• Session Date (YYYY-MM-DD format)\n• Start Time (HH:MM)\n• End Time (HH:MM)\n\nOptional fields:\n• Course Title\n• Trainer Name\n• Delivery Mode\n• Location/URL'
        }
      ]
    },
    {
      title: 'Partners & TSPs',
      icon: Building2,
      description: 'Managing partner organizations and training providers',
      topics: [
        { 
          question: 'How do I add a new Partner?', 
          answer: 'Navigate to Partners and click "Add Partner". Enter the partner name, select the region and country, and save.',
          roles: ['Admin', 'Team Manager']
        },
        { 
          question: 'What is a TSP?', 
          answer: 'A TSP (Training Service Provider) is an organization that delivers training on behalf of Microsoft. TSPs are assigned to Training Projects and linked to Purchase Orders for billing.'
        }
      ]
    },
    {
      title: 'Purchase Orders',
      icon: Receipt,
      description: 'Managing blanket POs and billing',
      topics: [
        { 
          question: 'Who can access Purchase Order management?', 
          answer: 'Purchase Order management is available to:\n• Super Admins\n• Admins\n• Team Managers (Microsoft)\n\nProject Managers do not have access to PO management.',
          roles: ['Admin', 'Team Manager']
        },
        { 
          question: 'What is a Blanket PO?', 
          answer: 'A Blanket Purchase Order is a pre-approved budget allocation for training services with a specific TSP. It has a total amount and tracks how much has been used across multiple training projects.'
        },
        { 
          question: 'How is PO balance tracked?', 
          answer: 'When you assign a PO to a Training Project and enter the project cost, the system automatically updates the used amount. The remaining balance is shown when selecting POs for new projects.'
        }
      ]
    },
    {
      title: 'User Management',
      icon: Users,
      description: 'Managing user access and roles',
      topics: [
        { 
          question: 'What user roles are available?', 
          answer: 'There are four roles:\n\n1. Super Admin - Full system access (Technofocus only)\n2. Admin - Full management capabilities (Technofocus users)\n3. Team Manager - Project and user management (Microsoft only)\n4. Project Manager - Assigned project management'
        },
        { 
          question: 'How do I approve a new user?', 
          answer: 'Go to User Management and click on "Pending Approval" tab. Review the user details and click "Approve" or "Deny".\n\nNote: Team Managers can only approve non-Technofocus users.',
          roles: ['Admin', 'Team Manager']
        },
        { 
          question: 'How do I promote a PM to Team Manager?', 
          answer: 'Only microsoft.com users can be promoted to Team Manager.\n\n1. Go to User Management\n2. Find the PM with a @microsoft.com email\n3. Click the actions menu (⋮)\n4. Select "Promote to Team Manager"',
          roles: ['Admin']
        },
        { 
          question: 'Can I disable my own account?', 
          answer: 'No, users cannot disable or delete their own account. This is a security measure to prevent accidental lockouts. Contact another admin to make changes to your account.'
        },
        { 
          question: 'Can admins modify Super Admin accounts?', 
          answer: 'No, regular admins cannot disable, delete, or modify Super Admin accounts. Only another Super Admin can make changes to Super Admin accounts.'
        }
      ]
    },
    {
      title: 'Audit Log',
      icon: History,
      description: 'Tracking system changes and user actions',
      topics: [
        { 
          question: 'What is the Audit Log?', 
          answer: 'The Audit Log tracks all important actions in the system including:\n• User approvals and denials\n• Role promotions and demotions\n• Account activations/deactivations\n• Domain settings changes\n\nThis helps with compliance and accountability.',
          roles: ['Admin']
        },
        { 
          question: 'How do I filter audit logs?', 
          answer: 'Use the filters at the top of the Audit Log page to:\n• Filter by action type (e.g., User Approved)\n• Search by actor email (who performed the action)\n• Search by target email (who was affected)'
        }
      ]
    },
    {
      title: 'Domain Settings',
      icon: Settings,
      description: 'Managing allowed email domains',
      topics: [
        { 
          question: 'How do I add a new domain?', 
          answer: 'Go to Settings > Domain Access Control.\n\n1. Enter the domain (e.g., company.com) in the input field\n2. Click "Add"\n\nUsers from that domain will now be able to register.',
          roles: ['Admin']
        },
        { 
          question: 'What does "Allow All Authenticated Domains" mean?', 
          answer: 'When enabled, any user who successfully authenticates via Microsoft Entra ID can access the system, regardless of their email domain. When disabled, only users from whitelisted domains can access the system.'
        }
      ]
    },
    {
      title: 'Feedback Management',
      icon: MessageSquare,
      description: 'Collecting and managing training feedback',
      topics: [
        { 
          question: 'How do I submit feedback for a session?', 
          answer: 'Open the Training Project and navigate to the Feedback tab. Click "Add Session Feedback" next to any delivered session. Rate the training quality, trainer effectiveness, content relevance, and provide comments.'
        },
        { 
          question: 'What is the MTM Score?', 
          answer: 'MTM (Metrics That Matter) Score is an industry-standard satisfaction measurement from www.metricsthatmatter.com. It rates overall training effectiveness from 1-10, measuring how well the training met expectations and delivered value to participants.'
        }
      ]
    }
  ];

  const filteredData = helpData.filter(section =>
    searchQuery === '' ||
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.topics.some(t => 
      t.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="space-y-8" data-testid="help-center-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime/10 mb-4">
          <HelpCircle className="w-8 h-8 text-lime" />
        </div>
        <h1 className="text-3xl font-bold mb-3">Help Center</h1>
        <p className="text-muted-foreground">
          Find answers to common questions and learn how to use Skilltrac effectively
        </p>
      </div>

      <Tabs defaultValue="guides" className="max-w-4xl mx-auto">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="guides">User Guides</TabsTrigger>
          <TabsTrigger value="roles">Role Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="guides" className="space-y-6 mt-6">
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for help topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
              />
            </div>
          </div>

          <div className="grid gap-6">
            {filteredData.map((section, index) => (
              <HelpSection key={index} {...section} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="roles" className="mt-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Role-Based Access Control</h2>
            <p className="text-muted-foreground">
              Skilltrac uses a role-based permission system. Each role has specific capabilities and restrictions.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {roleGuides.map((role, index) => (
              <RoleCard key={index} {...role} />
            ))}
          </div>

          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-blue-800 mb-2">Important Notes</h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li>• This application is used by <strong>Technofocus Project Managers</strong> to manage training requests</li>
                <li>• <strong>Super Admin</strong> role is reserved for senior Technofocus administrators</li>
                <li>• Only Super Admins can promote PMs to Admin or Admins to Super Admin</li>
                <li>• Users cannot disable or delete their own accounts</li>
                <li>• All role changes are recorded in the Audit Log for compliance</li>
                <li>• New users require approval from an Admin before they can access the system</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="bg-lime/5 border-lime/20 max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="font-semibold mb-2">Still need help?</h3>
            <p className="text-muted-foreground mb-4">
              Contact our support team for additional assistance
            </p>
            <a href="mailto:contact@technofocus.ai" className="text-lime hover:underline">
              contact@technofocus.ai
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenterPage;
